import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { AppHeader } from "@/components/AppHeader";
import { toast } from "sonner";
import {
  ArrowLeft,
  Play,
  Send,
  Loader2,
  CheckCircle2,
  XCircle,
  Trophy,
  Code2,
  Sparkles,
} from "lucide-react";
import { runPython } from "@/lib/pyodideLoader";

function ChallengeCard({ challenge, attempt, onSubmitted }) {
  const [code, setCode] = useState(challenge.starter || "");
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const isPython = (challenge.language || "python") === "python";
  const passedBefore = !!attempt?.passed;
  const passedNow = result?.passed;
  const passed = passedNow || passedBefore;

  const runLocal = async () => {
    if (!code.trim()) return;
    setRunning(true);
    setOutput(null);
    try {
      if (isPython) {
        const r = await runPython(code);
        setOutput(r);
      } else {
        setOutput({
          ok: false,
          error: "Local preview is only available for Python. Use Submit to check.",
        });
      }
    } finally {
      setRunning(false);
    }
  };

  const submit = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post(`/challenges/${challenge.id}/check`, { code });
      setResult(data);
      if (data.passed) {
        toast.success("Challenge passed! Great work 🎉");
      } else if (data.ok) {
        toast.error("Output didn't match. Check the expected output and try again.");
      } else {
        toast.error(data.error || "Couldn't run your code. Try again.");
      }
      onSubmitted?.();
    } catch (e) {
      if (!e.isSubscriptionError) {
        toast.error(e?.response?.data?.detail || "Submission failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`challenge-card-${challenge.id}`}
      className={`tactile-card p-5 sm:p-6 border-2 ${
        passed
          ? "border-[hsl(var(--brimi-mint))]/60 bg-[hsl(var(--brimi-mint))]/5"
          : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">
            <Code2 className="w-3 h-3" />
            {challenge.language || "python"} challenge
          </div>
          <h3 className="text-lg sm:text-xl font-black leading-snug">
            {challenge.title}
          </h3>
        </div>
        {passed ? (
          <span
            data-testid={`challenge-passed-${challenge.id}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[hsl(var(--brimi-mint))] text-foreground text-xs font-black"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Passed
          </span>
        ) : attempt?.attempts ? (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-foreground/10 text-foreground text-xs font-bold">
            {attempt.attempts} tries
          </span>
        ) : null}
      </div>

      <p className="text-sm sm:text-base text-foreground/80 mb-3">
        {challenge.prompt}
      </p>

      <textarea
        data-testid={`challenge-code-${challenge.id}`}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        spellCheck={false}
        className="w-full bg-foreground/95 text-background font-mono text-xs sm:text-sm p-3 rounded-xl outline-none resize-y min-h-[140px] max-h-[40vh]"
        placeholder={challenge.starter || ""}
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {isPython && (
          <button
            type="button"
            onClick={runLocal}
            disabled={running || !code.trim()}
            data-testid={`challenge-run-${challenge.id}`}
            className="pill-btn bg-[hsl(var(--brimi-mint))] hover:bg-[hsl(var(--brimi-mint))]/90 text-foreground text-sm px-4 py-1.5 disabled:opacity-50"
          >
            {running ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4 fill-current" />
            )}
            {running ? "Running…" : "Run"}
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !code.trim()}
          data-testid={`challenge-submit-${challenge.id}`}
          className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white text-sm px-4 py-1.5 disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {submitting ? "Checking…" : "Submit"}
        </button>
      </div>

      {output && (
        <div
          data-testid={`challenge-output-${challenge.id}`}
          className={`mt-3 px-3 py-2 rounded-xl text-xs sm:text-sm font-mono whitespace-pre-wrap ${
            output.ok ? "bg-[hsl(var(--brimi-mint))]/15" : "bg-destructive/10"
          }`}
        >
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
            {output.ok ? "Local output" : "Error"}
          </div>
          {output.stdout && <div>{output.stdout}</div>}
          {output.stderr && <div className="text-destructive">{output.stderr}</div>}
          {output.error && !output.ok && (
            <div className="text-destructive mt-1">{output.error}</div>
          )}
          {output.ok && !output.stdout && !output.stderr && (
            <div className="opacity-60 italic">
              (no output — try adding a print statement)
            </div>
          )}
        </div>
      )}

      {result && (
        <div
          data-testid={`challenge-result-${challenge.id}`}
          className={`mt-3 p-3 rounded-xl border-2 ${
            result.passed
              ? "border-[hsl(var(--brimi-mint))] bg-[hsl(var(--brimi-mint))]/10"
              : "border-[hsl(var(--brimi-coral))]/50 bg-[hsl(var(--brimi-coral))]/5"
          }`}
        >
          <div className="flex items-center gap-2 font-black text-sm mb-2">
            {result.passed ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-[hsl(var(--brimi-mint-strong,140_60%_35%))]" />
                Passed!
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-[hsl(var(--brimi-coral))]" />
                Not quite — check the output
              </>
            )}
          </div>
          {!result.passed && result.ok && (
            <div className="text-xs space-y-1 font-mono">
              <div>
                <span className="opacity-60">Expected:</span>{" "}
                <span className="font-bold whitespace-pre-wrap">
                  {result.expected || "(empty)"}
                </span>
              </div>
              <div>
                <span className="opacity-60">Your output:</span>{" "}
                <span className="font-bold whitespace-pre-wrap">
                  {result.actual || "(empty)"}
                </span>
              </div>
            </div>
          )}
          {!result.ok && (
            <div className="text-xs font-mono text-destructive whitespace-pre-wrap">
              {result.stderr || result.error || "Compilation/runtime error"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChallengesPage() {
  const { topicId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    api
      .get(`/challenges/${topicId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (!e.isSubscriptionError) {
          toast.error(e?.response?.data?.detail || "Failed to load challenges");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [topicId]);

  const refresh = () => load({ silent: true });

  const challenges = data?.challenges || [];
  const attempts = data?.attempts || {};
  const passedCount = challenges.filter((c) => attempts[c.id]?.passed).length;
  const total = challenges.length;
  const allDone = total > 0 && passedCount === total;

  // Derive back link to chapter list. Topic IDs use a numeric class (11/12) but the
  // syllabus uses Roman numerals (XI/XII), so map them explicitly.
  const ARABIC_TO_ROMAN = { "9": "IX", "10": "X", "11": "XI", "12": "XII" };
  const SUBJECT_PREFIX_TO_SLUG = { comp: "computer", eng: "english", math: "mathematics" };
  const m = (topicId || "").match(/^([a-z]+)-(\d+)/);
  const subjectSlug = m ? (SUBJECT_PREFIX_TO_SLUG[m[1]] || m[1]) : null;
  const classSlug = m ? (ARABIC_TO_ROMAN[m[2]] || m[2]) : null;
  const backTo = m ? `/subjects/${subjectSlug}/classes/${classSlug}/topics` : "/subjects";

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to={backTo}
          data-testid="challenges-back-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to chapters
        </Link>

        <div className="mb-8">
          <div className="text-sm font-bold uppercase tracking-widest text-[hsl(var(--brimi-blue))] mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Coding Challenges
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2">
            {data?.topic_title || "Challenges"}
          </h1>
          <p className="text-muted-foreground">
            Tiny coding puzzles you can solve right in the browser. Submit to check
            your output against the expected answer.
          </p>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading challenges…</div>
        ) : total === 0 ? (
          <div
            data-testid="challenges-empty"
            className="tactile-card p-10 text-center"
          >
            <div className="text-5xl mb-3">🧩</div>
            <div className="text-lg font-bold mb-1">
              No challenges yet for this chapter
            </div>
            <div className="text-sm text-muted-foreground">
              We&apos;re adding more soon. Try the Quiz or Notes in the meantime.
            </div>
          </div>
        ) : (
          <>
            <div
              data-testid="challenges-progress"
              className={`tactile-card p-4 sm:p-5 mb-6 flex items-center justify-between gap-3 ${
                allDone
                  ? "bg-[hsl(var(--brimi-mint))]/15 border-[hsl(var(--brimi-mint))]/60"
                  : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    allDone
                      ? "bg-[hsl(var(--brimi-mint))] text-foreground"
                      : "bg-foreground/5 text-foreground"
                  }`}
                >
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Your progress
                  </div>
                  <div className="text-lg font-black">
                    {passedCount} / {total} solved
                  </div>
                </div>
              </div>
              {allDone && (
                <div className="text-xs sm:text-sm font-black uppercase tracking-wider">
                  🎉 All done!
                </div>
              )}
            </div>

            <div className="space-y-4">
              {challenges.map((c) => (
                <ChallengeCard
                  key={c.id}
                  challenge={c}
                  attempt={attempts[c.id]}
                  onSubmitted={refresh}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
